import { useEffect, useState, useCallback } from 'react';
import { Textbox, FabricText } from 'fabric';
import { Copy, RefreshCw } from 'lucide-react';
import { useCanvasStore } from '../../stores/canvasStore';
import { useStudioStore } from '../../stores/studioStore';

interface DiagnosticReading {
  // Element identity
  elementId: string;
  elementType: string;
  text: string;
  textLength: number;

  // Store values (designer intent)
  storeWidth: number;
  storeHeight: number;
  storeFontSize: number;
  storeFontFamily: string;
  storeFontWeight: string | number;
  storeFontStyle: string;
  storeTextAlign: string;
  storeLetterSpacing: number;
  storeWidthIntent: string;
  storeTextFitMode: string;

  // Fabric object values
  fabricWidth: number;
  fabricHeight: number;
  fabricLeft: number;
  fabricTop: number;
  fabricFontSize: number;
  fabricFontFamily: string;
  fabricFontWeight: string | number;
  fabricFontStyle: string;
  fabricCharSpacing: number;
  fabricTextAlign: string;
  fabricText: string;

  // Internal Fabric measurement
  fabricLineCount: number;
  fabricLine0Text: string;
  fabricInternalLineWidth: number | null;  // _measureLine(0).width if available
  fabricCalcTextWidth: number | null;       // calcTextWidth() if available
  fabricBoundingRect: { left: number; top: number; width: number; height: number } | null;

  // Canvas2D measurement (mirrors getTextMinWidth logic)
  canvas2dFontString: string;
  canvas2dLine0Width: number;
  canvas2dActualBoundingBoxLeft: number;
  canvas2dActualBoundingBoxRight: number;
  canvas2dInkWidth: number;

  // Canvas2D with normalized fontStyle ('normal' instead of stored value)
  canvas2dNormalizedFontString: string;
  canvas2dNormalizedLine0Width: number;

  // Font readiness checks
  documentFontsReady: boolean;
  fontCheckExactTriple: boolean;       // "${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}"
  fontCheckCanonicalNormal: boolean;   // "normal ${fontWeight} ${fontSize}px ${fontFamily}"
  fontCheckJustFamily: boolean;        // "${fontSize}px ${fontFamily}"
  loadedFontFaces: string[];           // All FontFaces currently in document.fonts that match family

  // Disagreement deltas
  deltaCanvas2dVsFabricInternal: number | null;
  deltaCanvas2dVsFabricWidth: number;
  deltaStoreWidthVsFabricWidth: number;
}

function buildCanvas2dFontString(fontStyle: string, fontWeight: string | number, fontSize: number, fontFamily: string): string {
  return `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
}

function measureWithCanvas2d(text: string, fontString: string): { width: number; actualLeft: number; actualRight: number; inkWidth: number } {
  if (typeof document === 'undefined') {
    return { width: 0, actualLeft: 0, actualRight: 0, inkWidth: 0 };
  }
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');
  if (!ctx) return { width: 0, actualLeft: 0, actualRight: 0, inkWidth: 0 };
  ctx.font = fontString;
  const m = ctx.measureText(text);
  const width = m.width;
  const actualLeft = (m as TextMetrics & { actualBoundingBoxLeft?: number }).actualBoundingBoxLeft ?? 0;
  const actualRight = (m as TextMetrics & { actualBoundingBoxRight?: number }).actualBoundingBoxRight ?? width;
  return { width, actualLeft, actualRight, inkWidth: actualLeft + actualRight };
}

function listLoadedFontFaces(family: string): string[] {
  const out: string[] = [];
  const fontset = (document as unknown as { fonts?: FontFaceSet }).fonts;
  if (!fontset || typeof fontset.forEach !== 'function') return out;
  try {
    fontset.forEach((face: FontFace) => {
      const fam = face.family.replace(/['"]/g, '');
      if (fam.toLowerCase().includes(family.toLowerCase()) || family.toLowerCase().includes(fam.toLowerCase())) {
        out.push(`${face.family} ${face.weight} ${face.style} status=${face.status}`);
      }
    });
  } catch {
    // ignore
  }
  return out;
}

function safeFontCheck(fontString: string): boolean {
  const fontset = (document as unknown as { fonts?: FontFaceSet }).fonts;
  if (!fontset || typeof fontset.check !== 'function') return false;
  try {
    return fontset.check(fontString);
  } catch {
    return false;
  }
}

function readDiagnostic(): DiagnosticReading | null {
  const fc = useCanvasStore.getState().fabricCanvas;
  if (!fc) return null;
  const active = fc.getActiveObject();
  if (!active) return null;
  if (!(active instanceof Textbox) && !(active instanceof FabricText)) return null;

  const ELEMENT_ID_KEY = '_csElementId';
  const elementId = (active as unknown as Record<string, unknown>)[ELEMENT_ID_KEY] as string | undefined;
  if (!elementId) return null;

  const storeEl = useStudioStore.getState().canvasState.elements.find((e) => e.id === elementId);
  if (!storeEl || storeEl.type !== 'text') return null;

  const textEl = storeEl as typeof storeEl & {
    text: string;
    width: number;
    height: number;
    fontSize: number;
    fontFamily: string;
    fontWeight: string | number;
    fontStyle: string;
    textAlign: string;
    letterSpacing: number;
    widthIntent?: string;
    textFitMode?: string;
  };

  const fabricObj = active as Textbox | FabricText;
  const fabricText = String((fabricObj as unknown as { text?: string }).text ?? '');
  const fabricFontStyle = String((fabricObj as unknown as { fontStyle?: string }).fontStyle ?? 'normal');
  const fabricFontWeight = (fabricObj as unknown as { fontWeight?: string | number }).fontWeight ?? 400;
  const fabricFontSize = (fabricObj as unknown as { fontSize?: number }).fontSize ?? 16;
  const fabricFontFamily = String((fabricObj as unknown as { fontFamily?: string }).fontFamily ?? 'Arial');
  const fabricCharSpacing = (fabricObj as unknown as { charSpacing?: number }).charSpacing ?? 0;
  const fabricTextAlign = String((fabricObj as unknown as { textAlign?: string }).textAlign ?? 'left');

  // First line of text for measurement
  const line0 = (textEl.text || '').split('\n')[0] || '';

  // Fabric internal measurements
  let fabricInternalLineWidth: number | null = null;
  let fabricCalcTextWidth: number | null = null;
  try {
    const m = (fabricObj as unknown as { _measureLine?: (i: number) => { width: number } })._measureLine;
    if (typeof m === 'function') {
      const result = m.call(fabricObj, 0);
      if (result && typeof result.width === 'number') {
        fabricInternalLineWidth = result.width;
      }
    }
  } catch {
    // ignore
  }
  try {
    const c = (fabricObj as unknown as { calcTextWidth?: () => number }).calcTextWidth;
    if (typeof c === 'function') {
      fabricCalcTextWidth = c.call(fabricObj);
    }
  } catch {
    // ignore
  }

  let fabricBoundingRect: { left: number; top: number; width: number; height: number } | null = null;
  try {
    const br = (fabricObj as unknown as { getBoundingRect?: (a?: boolean, b?: boolean) => { left: number; top: number; width: number; height: number } }).getBoundingRect;
    if (typeof br === 'function') {
      fabricBoundingRect = br.call(fabricObj, true, true);
    }
  } catch {
    // ignore
  }

  // Canvas2D measurement using STORED font triple (matches getTextMinWidth)
  const storedFontString = buildCanvas2dFontString(textEl.fontStyle, textEl.fontWeight, textEl.fontSize, textEl.fontFamily);
  const storedMeasurement = measureWithCanvas2d(line0, storedFontString);

  // Canvas2D measurement using CANONICAL normalized font string
  const normalizedFontString = buildCanvas2dFontString('normal', textEl.fontWeight, textEl.fontSize, textEl.fontFamily);
  const normalizedMeasurement = measureWithCanvas2d(line0, normalizedFontString);

  // Font readiness checks
  const fontCheckExact = safeFontCheck(`${textEl.fontStyle} ${textEl.fontWeight} ${textEl.fontSize}px ${textEl.fontFamily}`);
  const fontCheckCanonical = safeFontCheck(`normal ${textEl.fontWeight} ${textEl.fontSize}px ${textEl.fontFamily}`);
  const fontCheckJustFam = safeFontCheck(`${textEl.fontSize}px ${textEl.fontFamily}`);
  const documentFontsReady = (() => {
    const fontset = (document as unknown as { fonts?: FontFaceSet & { ready?: Promise<unknown>; status?: string } }).fonts;
    if (!fontset) return false;
    return fontset.status === 'loaded';
  })();
  const loadedFontFaces = listLoadedFontFaces(textEl.fontFamily);

  // Deltas
  const deltaCanvas2dVsInternal = fabricInternalLineWidth != null
    ? storedMeasurement.width - fabricInternalLineWidth
    : null;
  const deltaCanvas2dVsFabricWidth = storedMeasurement.width - ((fabricObj as unknown as { width?: number }).width ?? 0);
  const deltaStoreVsFabric = textEl.width - ((fabricObj as unknown as { width?: number }).width ?? 0);

  return {
    elementId,
    elementType: storeEl.type,
    text: textEl.text || '',
    textLength: (textEl.text || '').length,

    storeWidth: textEl.width,
    storeHeight: textEl.height,
    storeFontSize: textEl.fontSize,
    storeFontFamily: textEl.fontFamily,
    storeFontWeight: textEl.fontWeight,
    storeFontStyle: textEl.fontStyle,
    storeTextAlign: textEl.textAlign,
    storeLetterSpacing: textEl.letterSpacing,
    storeWidthIntent: textEl.widthIntent ?? 'unspecified',
    storeTextFitMode: textEl.textFitMode ?? 'unspecified',

    fabricWidth: (fabricObj as unknown as { width?: number }).width ?? 0,
    fabricHeight: (fabricObj as unknown as { height?: number }).height ?? 0,
    fabricLeft: (fabricObj as unknown as { left?: number }).left ?? 0,
    fabricTop: (fabricObj as unknown as { top?: number }).top ?? 0,
    fabricFontSize,
    fabricFontFamily,
    fabricFontWeight,
    fabricFontStyle,
    fabricCharSpacing,
    fabricTextAlign,
    fabricText,

    fabricLineCount: ((fabricObj as unknown as { _textLines?: unknown[] })._textLines ?? []).length,
    fabricLine0Text: (((fabricObj as unknown as { _textLines?: string[][] })._textLines ?? [])[0] ?? []).join(''),
    fabricInternalLineWidth,
    fabricCalcTextWidth,
    fabricBoundingRect,

    canvas2dFontString: storedFontString,
    canvas2dLine0Width: storedMeasurement.width,
    canvas2dActualBoundingBoxLeft: storedMeasurement.actualLeft,
    canvas2dActualBoundingBoxRight: storedMeasurement.actualRight,
    canvas2dInkWidth: storedMeasurement.inkWidth,

    canvas2dNormalizedFontString: normalizedFontString,
    canvas2dNormalizedLine0Width: normalizedMeasurement.width,

    documentFontsReady,
    fontCheckExactTriple: fontCheckExact,
    fontCheckCanonicalNormal: fontCheckCanonical,
    fontCheckJustFamily: fontCheckJustFam,
    loadedFontFaces,

    deltaCanvas2dVsFabricInternal: deltaCanvas2dVsInternal,
    deltaCanvas2dVsFabricWidth,
    deltaStoreWidthVsFabricWidth: deltaStoreVsFabric,
  };
}

export function TextMeasurementDiagnostic() {
  const [reading, setReading] = useState<DiagnosticReading | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const fabricCanvas = useCanvasStore((s) => s.fabricCanvas);

  const refresh = useCallback(() => {
    setReading(readDiagnostic());
  }, []);

  useEffect(() => {
    if (!fabricCanvas) return;

    const handleSelection = () => {
      requestAnimationFrame(() => {
        refresh();
      });
    };
    const handleCleared = () => {
      setReading(null);
    };

    fabricCanvas.on('selection:created', handleSelection);
    fabricCanvas.on('selection:updated', handleSelection);
    fabricCanvas.on('selection:cleared', handleCleared);

    // Initial read
    refresh();

    return () => {
      fabricCanvas.off('selection:created', handleSelection);
      fabricCanvas.off('selection:updated', handleSelection);
      fabricCanvas.off('selection:cleared', handleCleared);
    };
  }, [fabricCanvas, refresh]);

  const handleCopy = useCallback(async () => {
    if (!reading) return;
    const lines: string[] = [];
    lines.push('=== TEXT MEASUREMENT DIAGNOSTIC ===');
    lines.push(`Generated: ${new Date().toISOString()}`);
    lines.push('');
    lines.push('--- Element Identity ---');
    lines.push(`elementId: ${reading.elementId}`);
    lines.push(`type: ${reading.elementType}`);
    lines.push(`text: "${reading.text}"`);
    lines.push(`textLength: ${reading.textLength}`);
    lines.push('');
    lines.push('--- Store Values (Designer Intent) ---');
    lines.push(`storeWidth: ${reading.storeWidth}`);
    lines.push(`storeHeight: ${reading.storeHeight}`);
    lines.push(`storeFontSize: ${reading.storeFontSize}`);
    lines.push(`storeFontFamily: ${reading.storeFontFamily}`);
    lines.push(`storeFontWeight: ${reading.storeFontWeight}`);
    lines.push(`storeFontStyle: ${reading.storeFontStyle}`);
    lines.push(`storeTextAlign: ${reading.storeTextAlign}`);
    lines.push(`storeLetterSpacing: ${reading.storeLetterSpacing}`);
    lines.push(`storeWidthIntent: ${reading.storeWidthIntent}`);
    lines.push(`storeTextFitMode: ${reading.storeTextFitMode}`);
    lines.push('');
    lines.push('--- Fabric Object Values ---');
    lines.push(`fabricWidth: ${reading.fabricWidth}`);
    lines.push(`fabricHeight: ${reading.fabricHeight}`);
    lines.push(`fabricLeft: ${reading.fabricLeft}`);
    lines.push(`fabricTop: ${reading.fabricTop}`);
    lines.push(`fabricFontSize: ${reading.fabricFontSize}`);
    lines.push(`fabricFontFamily: ${reading.fabricFontFamily}`);
    lines.push(`fabricFontWeight: ${reading.fabricFontWeight}`);
    lines.push(`fabricFontStyle: ${reading.fabricFontStyle}`);
    lines.push(`fabricCharSpacing: ${reading.fabricCharSpacing}`);
    lines.push(`fabricTextAlign: ${reading.fabricTextAlign}`);
    lines.push(`fabricText: "${reading.fabricText}"`);
    lines.push('');
    lines.push('--- Fabric Internal Measurement ---');
    lines.push(`fabricLineCount: ${reading.fabricLineCount}`);
    lines.push(`fabricLine0Text: "${reading.fabricLine0Text}"`);
    lines.push(`fabricInternalLineWidth (_measureLine(0).width): ${reading.fabricInternalLineWidth}`);
    lines.push(`fabricCalcTextWidth(): ${reading.fabricCalcTextWidth}`);
    lines.push(`fabricBoundingRect: ${JSON.stringify(reading.fabricBoundingRect)}`);
    lines.push('');
    lines.push('--- Canvas2D Measurement (Stored Font Triple) ---');
    lines.push(`canvas2dFontString: "${reading.canvas2dFontString}"`);
    lines.push(`canvas2dLine0Width: ${reading.canvas2dLine0Width}`);
    lines.push(`canvas2dActualBoundingBoxLeft: ${reading.canvas2dActualBoundingBoxLeft}`);
    lines.push(`canvas2dActualBoundingBoxRight: ${reading.canvas2dActualBoundingBoxRight}`);
    lines.push(`canvas2dInkWidth: ${reading.canvas2dInkWidth}`);
    lines.push('');
    lines.push('--- Canvas2D Measurement (Normalized fontStyle="normal") ---');
    lines.push(`canvas2dNormalizedFontString: "${reading.canvas2dNormalizedFontString}"`);
    lines.push(`canvas2dNormalizedLine0Width: ${reading.canvas2dNormalizedLine0Width}`);
    lines.push('');
    lines.push('--- Font Load State ---');
    lines.push(`documentFontsReady: ${reading.documentFontsReady}`);
    lines.push(`fontCheckExactTriple ("${reading.storeFontStyle} ${reading.storeFontWeight} ${reading.storeFontSize}px ${reading.storeFontFamily}"): ${reading.fontCheckExactTriple}`);
    lines.push(`fontCheckCanonicalNormal ("normal ${reading.storeFontWeight} ${reading.storeFontSize}px ${reading.storeFontFamily}"): ${reading.fontCheckCanonicalNormal}`);
    lines.push(`fontCheckJustFamily ("${reading.storeFontSize}px ${reading.storeFontFamily}"): ${reading.fontCheckJustFamily}`);
    lines.push(`loadedFontFaces:`);
    if (reading.loadedFontFaces.length === 0) {
      lines.push(`  (none matched)`);
    } else {
      for (const f of reading.loadedFontFaces) {
        lines.push(`  - ${f}`);
      }
    }
    lines.push('');
    lines.push('--- Disagreement Deltas ---');
    lines.push(`deltaCanvas2dVsFabricInternal (canvas2d - fabricInternal): ${reading.deltaCanvas2dVsFabricInternal}`);
    lines.push(`deltaCanvas2dVsFabricWidth (canvas2d - fabricWidth): ${reading.deltaCanvas2dVsFabricWidth}`);
    lines.push(`deltaStoreWidthVsFabricWidth (storeWidth - fabricWidth): ${reading.deltaStoreWidthVsFabricWidth}`);
    lines.push('');
    lines.push('=== END DIAGNOSTIC ===');

    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      // Fallback: select the text in a hidden textarea
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopyState('copied');
        setTimeout(() => setCopyState('idle'), 1500);
      } catch {
        // give up silently
      }
      document.body.removeChild(ta);
    }
  }, [reading]);

  if (!reading) {
    return (
      <div
        style={{
          background: 'rgba(20, 20, 24, 0.96)',
          color: '#e5e7eb',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: 8,
          padding: '10px 14px',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 11,
          maxWidth: 360,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontWeight: 600, color: '#fbbf24' }}>Text Measurement Diagnostic</span>
        </div>
        <div style={{ color: '#9ca3af' }}>Select a text element on the canvas to inspect.</div>
      </div>
    );
  }

  const Row = ({ label, value, highlight }: { label: string; value: string | number | boolean | null | undefined; highlight?: boolean }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dotted rgba(255,255,255,0.06)' }}>
      <span style={{ color: '#9ca3af' }}>{label}</span>
      <span style={{ color: highlight ? '#fbbf24' : '#e5e7eb', fontWeight: highlight ? 600 : 400, marginLeft: 12, textAlign: 'right', wordBreak: 'break-all' }}>
        {value === null || value === undefined ? '—' : String(value)}
      </span>
    </div>
  );

  const SectionHeader = ({ children }: { children: string }) => (
    <div style={{ marginTop: 10, marginBottom: 4, color: '#fbbf24', fontWeight: 700, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}>
      {children}
    </div>
  );

  const deltaInternalAbs = reading.deltaCanvas2dVsFabricInternal != null ? Math.abs(reading.deltaCanvas2dVsFabricInternal) : 0;
  const deltaStoreFabricAbs = Math.abs(reading.deltaStoreWidthVsFabricWidth);

  return (
    <div
      style={{
        background: 'rgba(15, 15, 18, 0.97)',
        color: '#e5e7eb',
        border: '1px solid rgba(255, 255, 255, 0.14)',
        borderRadius: 10,
        padding: '12px 14px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11,
        width: 420,
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.5)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontWeight: 700, color: '#fbbf24' }}>Text Measurement Diagnostic</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={refresh}
            title="Refresh"
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#9ca3af', cursor: 'pointer', padding: '2px 6px', borderRadius: 4 }}
          >
            <RefreshCw size={12} />
          </button>
          <button
            onClick={handleCopy}
            title="Copy diagnostic to clipboard"
            style={{
              background: copyState === 'copied' ? 'rgba(34, 197, 94, 0.2)' : 'transparent',
              border: '1px solid rgba(255,255,255,0.15)',
              color: copyState === 'copied' ? '#4ade80' : '#9ca3af',
              cursor: 'pointer',
              padding: '2px 6px',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Copy size={12} />
            <span style={{ fontSize: 10 }}>{copyState === 'copied' ? 'COPIED' : 'COPY'}</span>
          </button>
        </div>
      </div>

      <SectionHeader>Element</SectionHeader>
      <Row label="id" value={reading.elementId.slice(0, 8) + '...'} />
      <Row label="text" value={`"${reading.text}"`} />
      <Row label="textLen" value={reading.textLength} />

      <SectionHeader>Store (Designer Intent)</SectionHeader>
      <Row label="width" value={reading.storeWidth} />
      <Row label="height" value={reading.storeHeight} />
      <Row label="fontSize" value={reading.storeFontSize} />
      <Row label="fontFamily" value={reading.storeFontFamily} />
      <Row label="fontWeight" value={reading.storeFontWeight} />
      <Row label="fontStyle" value={reading.storeFontStyle} highlight={reading.storeFontStyle !== 'normal' && reading.storeFontStyle !== 'italic' && reading.storeFontStyle !== 'oblique'} />
      <Row label="textAlign" value={reading.storeTextAlign} />
      <Row label="letterSpacing" value={reading.storeLetterSpacing} />
      <Row label="widthIntent" value={reading.storeWidthIntent} />
      <Row label="textFitMode" value={reading.storeTextFitMode} />

      <SectionHeader>Fabric Object</SectionHeader>
      <Row label="width" value={reading.fabricWidth} />
      <Row label="height" value={reading.fabricHeight} />
      <Row label="fontSize" value={reading.fabricFontSize} />
      <Row label="fontFamily" value={reading.fabricFontFamily} />
      <Row label="fontWeight" value={reading.fabricFontWeight} />
      <Row label="fontStyle" value={reading.fabricFontStyle} />
      <Row label="charSpacing" value={reading.fabricCharSpacing} />
      <Row label="textAlign" value={reading.fabricTextAlign} />
      <Row label="text" value={`"${reading.fabricText}"`} />

      <SectionHeader>Fabric Internal Measurement</SectionHeader>
      <Row label="lineCount" value={reading.fabricLineCount} />
      <Row label="line0" value={`"${reading.fabricLine0Text}"`} />
      <Row label="_measureLine(0)" value={reading.fabricInternalLineWidth?.toFixed(3) ?? null} highlight />
      <Row label="calcTextWidth()" value={reading.fabricCalcTextWidth?.toFixed(3) ?? null} />
      <Row
        label="boundingRect"
        value={reading.fabricBoundingRect ? `${reading.fabricBoundingRect.width.toFixed(1)}x${reading.fabricBoundingRect.height.toFixed(1)}` : null}
      />

      <SectionHeader>Canvas2D (Stored Font Triple)</SectionHeader>
      <Row label="fontString" value={reading.canvas2dFontString} />
      <Row label="measureText" value={reading.canvas2dLine0Width.toFixed(3)} highlight />
      <Row label="actualLeft" value={reading.canvas2dActualBoundingBoxLeft.toFixed(3)} />
      <Row label="actualRight" value={reading.canvas2dActualBoundingBoxRight.toFixed(3)} />
      <Row label="inkWidth" value={reading.canvas2dInkWidth.toFixed(3)} />

      <SectionHeader>Canvas2D (Normalized "normal")</SectionHeader>
      <Row label="fontString" value={reading.canvas2dNormalizedFontString} />
      <Row label="measureText" value={reading.canvas2dNormalizedLine0Width.toFixed(3)} />

      <SectionHeader>Font Load State</SectionHeader>
      <Row label="document.fonts.ready" value={reading.documentFontsReady} highlight={!reading.documentFontsReady} />
      <Row label="check(exact triple)" value={reading.fontCheckExactTriple} highlight={!reading.fontCheckExactTriple} />
      <Row label="check(canonical normal)" value={reading.fontCheckCanonicalNormal} highlight={!reading.fontCheckCanonicalNormal} />
      <Row label="check(just family)" value={reading.fontCheckJustFamily} />
      <div style={{ marginTop: 4, color: '#9ca3af' }}>loadedFontFaces:</div>
      {reading.loadedFontFaces.length === 0 ? (
        <div style={{ color: '#ef4444', paddingLeft: 8 }}>(none matched)</div>
      ) : (
        reading.loadedFontFaces.map((f, i) => (
          <div key={i} style={{ paddingLeft: 8, color: '#e5e7eb', fontSize: 10 }}>{f}</div>
        ))
      )}

      <SectionHeader>Disagreement Deltas</SectionHeader>
      <Row
        label="canvas2d - fabricInternal"
        value={reading.deltaCanvas2dVsFabricInternal != null ? reading.deltaCanvas2dVsFabricInternal.toFixed(3) : null}
        highlight={deltaInternalAbs > 0.5}
      />
      <Row
        label="canvas2d - fabricWidth"
        value={reading.deltaCanvas2dVsFabricWidth.toFixed(3)}
      />
      <Row
        label="storeWidth - fabricWidth"
        value={reading.deltaStoreWidthVsFabricWidth.toFixed(3)}
        highlight={deltaStoreFabricAbs > 0.5}
      />
    </div>
  );
}
